import React from "react";

import { DeviceStatus, MdmDeviceDto, useGetAllDevicesByProjectQuery } from "gql/generated/types";
import { ColumnFilterElementTemplateOptions } from "primereact/column";
import { Dropdown } from "primereact/dropdown";
import { Link } from "react-router-dom";
import { DEFAULT_DATE_FORMATTER } from "utils/constants";
import { parseToLocalZoneDateTime } from "utils/time-utils";
import { TableColumn } from "utils/types";
import DeviceStatusTag from "../components/DeviceStatus";
import MainTable from "../components/MainTable";
import useProjectContext from "../hooks/useProjectContext";
import { handleDeviceStatus, rowDropdownFilterTemplate } from "../utils/utils";

const Devices = () => {
  const {projectId, project} = useProjectContext();

  const { data: deviceData, loading } = useGetAllDevicesByProjectQuery({
    variables: { id: projectId! },
    skip: !projectId,
  });

  const edit = (rowData: MdmDeviceDto) => (
    <Link to={`${rowData.id}`} title="Device Info">
      {rowData.identifier}
    </Link>
  );
  
  const handleLastMeasurementDate = (rowData: MdmDeviceDto) =>
    rowData.status.lastMeasurementDate &&
    parseToLocalZoneDateTime(rowData.status.lastMeasurementDate).format(DEFAULT_DATE_FORMATTER);
  
  const statusRowFilterTemplate = (options: ColumnFilterElementTemplateOptions) => {
    const statusOptionTemplate = (option: DeviceStatus) => <DeviceStatusTag status={option} />
    return (
      <Dropdown
        value={options.value}
        options={Object.values(DeviceStatus)}
        onChange={e => options.filterApplyCallback(e.value)}
        valueTemplate={options.value && <DeviceStatusTag status={options.value} />}
        itemTemplate={statusOptionTemplate}
        placeholder="Select One"
        showClear
      />
    );
  }

  const objectCodeTemplate = (rowData: MdmDeviceDto) => (
    <Link to={`/mdm/${projectId}/objects/${rowData.objectRelation?.mdmObject?.id}`}>
      {rowData.objectRelation?.mdmObject?.identifier}
    </Link>
  );

  const columns: TableColumn<MdmDeviceDto>[] = [
    { field: "identifier", header: "Identifier", body: edit, sortable: true, filter: true },
    {
      field: "objectRelation.mdmObject.identifier",
      header: "Object Code",
      body: objectCodeTemplate,
      sortable: true,
      filter: true,
    },
    {
      field: "objectRelation.mdmObject.address.fullAddress",
      header: "Object Address",
      sortable: true,
      filter: true,
    },
    {
      field: "objectRelation.roleInObject",
      header: "Type",
      body: rowData =>
        project?.mdmProjectById.configuration?.deviceConfiguration.measurementTypes?.find(
          type => type?.id === rowData.objectRelation?.roleInObject
        )?.title ?? "",
      sortable: true,
    },
    {
      field: "config.title",
      header: "Config",
      sortable: true,
      filter: true,
      filterElement: o =>
        rowDropdownFilterTemplate(o, [
          ...new Set(deviceData?.mdmDevicesByProjectId.map(d => d.config?.title)),
        ]),
      showFilterMenuOptions: false,
      showApplyButton: false,
      showClearButton: false,
    },
    {
      field: "status.status",
      header: "Status",
      body: handleDeviceStatus,
      filter: true,
      filterElement: statusRowFilterTemplate,
      showFilterMenuOptions: false,
      showApplyButton: false,
      showClearButton: false,
      sortable: true,
      hideInFile: true,
    },
    {
      field: "status.lastMeasurementDate",
      header: "Last Measurement Date",
      body: handleLastMeasurementDate,
      sortable: true,
    },
  ];

  return (
    <MainTable
      columns={columns}
      data={(deviceData?.mdmDevicesByProjectId ?? []) as MdmDeviceDto[]}
      tableName="Devices"
      searchFields={["identifier", "tags"]}
      loading={loading}
    />
  );
};

export default Devices;